import { useEffect, useMemo, useRef, useState } from "react";
import type { BoardBackground, BoardMeta } from "../../shared/model";
import { DEFAULT_BG } from "../../shared/model";
import { createEditor } from "../editor/create";
import type { Editor, EditorHost } from "../editor/editor";
import { elementLinks } from "../editor/elements";
import { makeThumbnail } from "../editor/export";
import { stickerUrl } from "../data/stickers";
import { BoardView } from "../board/BoardView";
import { navigate } from "../app/router";
import { useSession } from "../app/session";
import { api, ApiError, assetUrl } from "../sync/api";
import * as local from "../sync/localdb";
import { CloudSync, LocalSync, type BoardSync } from "../sync/sync";
import { Button, Spinner, useToast } from "../ui/kit";

type Kind = "cloud" | "guest" | "shared";

/** A note along the top of the board that can be put away for the rest of the session. */
function Banner({ id, children }: { id: string; children: React.ReactNode }) {
  const key = "sepia.banner." + id;
  const [hidden, setHidden] = useState(() => sessionStorage.getItem(key) === "1");
  if (hidden) return null;
  return (
    <div className="guest-banner" data-canvas-ignore>
      {children}
      <button
        type="button"
        className="banner-close"
        aria-label="Dismiss"
        onClick={() => {
          sessionStorage.setItem(key, "1");
          setHidden(true);
        }}
      >
        ×
      </button>
    </div>
  );
}

function metaCacheKey(kind: Kind, id: string) {
  return `sepia.meta.${kind}.${id}`;
}

function guestMeta(g: local.GuestBoard): BoardMeta {
  return { id: g.id, title: g.title, folderId: null, bg: g.bg, thumb: g.thumb, starred: false, trashedAt: null, shareToken: null, shareMode: null, links: [], elCount: g.elCount, createdAt: g.createdAt, updatedAt: g.updatedAt };
}

async function imageSize(blob: Blob): Promise<{ w: number; h: number }> {
  const bmp = await createImageBitmap(blob);
  const out = { w: bmp.width, h: bmp.height };
  bmp.close?.();
  return out;
}

export function BoardPage({ kind, id }: { kind: Kind; id: string }) {
  const { user } = useSession();
  const toast = useToast();
  const [meta, setMeta] = useState<BoardMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [, bump] = useState(0);
  const metaRef = useRef<BoardMeta | null>(null);
  metaRef.current = meta;
  const boardsCache = useRef<{ id: string; title: string }[] | null>(null);

  const knownBoards = async (): Promise<{ id: string; title: string }[]> => {
    if (boardsCache.current) return boardsCache.current;
    if (kind === "guest") boardsCache.current = (await local.listGuestBoards()).map((b) => ({ id: b.id, title: b.title }));
    else if (user) boardsCache.current = (await api.boards()).boards.filter((b) => !b.trashedAt).map((b) => ({ id: b.id, title: b.title }));
    else boardsCache.current = [];
    return boardsCache.current;
  };

  const editor: Editor = useMemo(() => {
    const host: EditorHost = {
      async uploadImage(blob) {
        const { w, h } = await imageSize(blob);
        if (kind === "guest") return { src: await local.putAsset(blob, w, h), w, h };
        const r = await api.uploadAsset(blob);
        return { src: r.id, w, h };
      },
      resolveImage: (src) => (src.startsWith("local-") ? local.localAssetUrl(src, () => bump((n) => n + 1)) : assetUrl(src)),
      async openBoardLink(title) {
        const list = await knownBoards().catch(() => []);
        const hit = list.find((b) => b.id === title) ?? list.find((b) => b.title.trim().toLowerCase() === title.trim().toLowerCase());
        if (hit) {
          navigate((kind === "guest" ? "/g/" : "/b/") + hit.id);
          return;
        }
        if (kind === "shared") {
          toast(`“${title}” isn't shared with you.`);
          return;
        }
        if (!window.confirm(`There's no board called “${title}” yet. Create it?`)) return;
        if (kind === "guest") {
          const g: local.GuestBoard = { id: crypto.randomUUID().slice(0, 16), title, bg: DEFAULT_BG, thumb: null, createdAt: Date.now(), updatedAt: Date.now(), elCount: 0 };
          await local.putGuestBoard(g);
          navigate("/g/" + g.id);
        } else {
          const r = await api.createBoard({ title, bg: metaRef.current?.bg });
          navigate("/b/" + r.board.id);
        }
      },
      toast
    };
    return createEditor(host);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, id]);

  useEffect(() => {
    // handle for the end-to-end tests and debugging (development builds only)
    if (import.meta.env.DEV) (window as unknown as { __sepia: Editor }).__sepia = editor;
  }, [editor]);

  const syncRef = useRef<BoardSync | null>(null);
  const [sync, setSync] = useState<BoardSync | null>(null);

  // load metadata, then start syncing
  useEffect(() => {
    let cancelled = false;
    let s: BoardSync | null = null;
    (async () => {
      let m: BoardMeta | null = null;
      try {
        if (kind === "cloud") {
          m = (await api.board(id)).board;
          localStorage.setItem(metaCacheKey(kind, id), JSON.stringify(m));
        } else if (kind === "shared") {
          m = (await api.shared(id)).board;
        } else {
          const g = await local.getGuestBoard(id);
          if (!g) throw new ApiError(404, "This board isn't on this device.");
          m = guestMeta(g);
        }
      } catch (e) {
        const err = e as ApiError;
        const cached = kind !== "guest" ? localStorage.getItem(metaCacheKey(kind, id)) : null;
        if (err.status === 0 && cached) m = JSON.parse(cached);
        else {
          if (!cancelled) setError(err.status === 401 ? "Please log in to open this board." : err.message);
          return;
        }
      }
      if (cancelled || !m) return;
      const readOnly = kind === "shared" && m.role === "viewer";
      editor.setBackground(m.bg);
      editor.setUi({ readOnly, tool: readOnly ? "hand" : editor.ui.tool === "laser" ? "pen" : editor.ui.tool });
      setMeta(m);
      document.title = `${m.title} · Sepia`;
      s = kind === "guest" ? new LocalSync(editor, id, (n) => saveGuest({ elCount: n })) : new CloudSync(editor, m.id, kind === "shared" ? { shareToken: id, readOnly } : {});
      syncRef.current = s;
      setSync(s);
      await s.start();
      if (cancelled) return;
      const camKey = `sepia.cam.${kind}.${id}`;
      // first visit: a notebook opens at the top of its first page, an endless board shows everything
      const firstView = () => {
        const first = editor.pages()[0];
        if (first) editor.fitPageWidth(first.id);
        else editor.zoomToFit(false);
      };
      let cam: { x: number; y: number; z: number } | null = null;
      try {
        cam = JSON.parse(localStorage.getItem(camKey) || "null");
      } catch {
        cam = null;
      }
      if (cam && Number.isFinite(cam.z)) {
        const c = cam;
        requestAnimationFrame(() => editor.camera.set(c.x, c.y, c.z));
      } else {
        requestAnimationFrame(firstView);
        if (!editor.isPaged()) {
          // a new notebook's first page may only arrive with the first sync
          const off = editor.doc.subscribe(() => {
            if (!editor.isPaged()) return;
            off();
            requestAnimationFrame(firstView);
          });
          window.setTimeout(off, 10000);
        }
      }
      // come back to the layer you were drawing on
      const layerKey = `sepia.layer.${kind}.${id}`;
      const savedLayer = localStorage.getItem(layerKey);
      const layers = editor.layers();
      editor.setActiveLayer(savedLayer && layers.some((l) => l.id === savedLayer) ? savedLayer : layers[layers.length - 1]?.id ?? "base");
      let lastLayer = editor.ui.activeLayer;
      editor.subscribeUi(() => {
        if (editor.ui.activeLayer !== lastLayer) {
          lastLayer = editor.ui.activeLayer;
          localStorage.setItem(layerKey, lastLayer);
        }
      });
      let camTimer = 0;
      editor.camera.subscribe(() => {
        window.clearTimeout(camTimer);
        camTimer = window.setTimeout(() => localStorage.setItem(camKey, JSON.stringify({ x: editor.camera.x, y: editor.camera.y, z: editor.camera.z })), 500);
      });
      setReady(true);
    })();
    return () => {
      cancelled = true;
      const current = syncRef.current;
      if (current) {
        saveIndex(true);
        current.flushNow(1500).finally(() => current.destroy());
      }
      syncRef.current = null;
      document.title = "Sepia";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, kind, id]);

  const saveGuest = async (patch: Partial<local.GuestBoard>) => {
    const g = await local.getGuestBoard(id);
    if (g) await local.putGuestBoard({ ...g, ...patch, updatedAt: Date.now() });
  };

  // search text, [[links]] and the thumbnail are refreshed a few seconds after edits settle
  const indexTimer = useRef(0);
  const saveIndex = async (immediate = false) => {
    const m = metaRef.current;
    if (!m || kind === "shared" || editor.ui.readOnly) return;
    const run = async () => {
      const els = editor.doc.all();
      const thumb = await makeThumbnail(els, m.bg, (s) => editor.host.resolveImage(s), stickerUrl, { layers: editor.layers(), layerOf: (e) => editor.layerOf(e) }, editor.pages()).catch(() => null);
      if (kind === "guest") {
        await saveGuest({ thumb, elCount: els.length });
        return;
      }
      const titles = new Set(els.flatMap(elementLinks).map((t) => t.toLowerCase()));
      let links: string[] = [];
      if (titles.size) {
        const list = await knownBoards().catch(() => []);
        links = list.filter((b) => titles.has(b.title.trim().toLowerCase()) || titles.has(b.id)).map((b) => b.id);
      }
      await api.patchBoard(m.id, { text: editor.searchText(), links, thumb, touch: true }).catch(() => undefined);
    };
    window.clearTimeout(indexTimer.current);
    if (immediate) run();
    else indexTimer.current = window.setTimeout(run, 5000);
  };

  useEffect(() => {
    if (!ready) return;
    return editor.doc.subscribe((c) => {
      if (c.origin === "local" || c.origin === "history") saveIndex();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, editor]);

  if (error) {
    return (
      <div className="page-center">
        <div className="lost-card">
          <img src="/art/empty-library.webp" alt="" width="300" />
          <h1>Hmm.</h1>
          <p>{error}</p>
          <div className="row-gap">
            <Button kind="primary" onClick={() => navigate(user ? "/" : "/login?next=" + encodeURIComponent(location.pathname))}>{user ? "Back to my boards" : "Log in"}</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!meta || !sync) {
    return (
      <div className="page-center">
        <Spinner label="Opening board" />
      </div>
    );
  }

  const updateMeta = (patch: Partial<BoardMeta>) => setMeta((m) => (m ? { ...m, ...patch } : m));

  return (
    <BoardView
      key={kind + id}
      editor={editor}
      sync={sync}
      meta={meta}
      kind={kind}
      backLabel={kind === "shared" && !user ? "About Sepia" : "All boards"}
      onBack={() => navigate(kind === "shared" && !user ? "/welcome" : "/")}
      onTitle={(title) => {
        updateMeta({ title });
        document.title = `${title} · Sepia`;
        boardsCache.current = null;
        if (kind === "guest") saveGuest({ title });
        else if (kind === "cloud") api.patchBoard(meta.id, { title }).catch((e) => toast(e.message, "error"));
      }}
      onBackground={(bg: BoardBackground) => {
        updateMeta({ bg });
        editor.setBackground(bg);
        if (kind === "guest") saveGuest({ bg });
        else if (kind === "cloud") api.patchBoard(meta.id, { bg }).catch((e) => toast(e.message, "error"));
      }}
      onMetaChange={updateMeta}
      banner={
        kind === "guest" && !user ? (
          <Banner id="guest">
            This board lives on this device only. <a href={"/signup?import=1"} onClick={(e) => { e.preventDefault(); navigate("/signup?import=1"); }}>Create a free account</a> to keep it everywhere.
          </Banner>
        ) : kind === "shared" && meta.role === "viewer" ? (
          <Banner id={"shared-" + id}>
            {meta.ownerName ? `${meta.ownerName}’s board · ` : ""}view only{meta.shareMode === "edit" && !user ? " — log in to edit" : ""}
          </Banner>
        ) : null
      }
    />
  );
}
